import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaFileExcel } from "react-icons/fa";
import { PiStudent } from "react-icons/pi";
import NavBar from "./NavMenu";

const Table = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const title = location.state?.title ?? "Class Size";

  const passedQuarters = location.state?.selectedQuarters || [
    "current_quarter 2025",
    "previous_quarter 2024",
  ];

  const [selectedQuarters] = useState(passedQuarters);
  const [mergedData, setMergedData] = useState([]);
  const [totalRow, setTotalRow] = useState({
    current_quarter: 0,
    previous_quarter: 0,
    variance: 0,
  });

  useEffect(() => {
    const currentData = location.state?.currentData || [];
    const previousData = location.state?.previousData || [];

    const normalizeData = (data) =>
      data.map((item) => ({
        label: item.label ?? item.course_code ?? "Unknown",
        y: parseFloat(item.y ?? item.totalWithdrawn ?? 0),
        course_level: item.course_level ?? "Uncategorized",
      }));

    const normalizedCurrent = normalizeData(currentData);
    const normalizedPrevious = normalizeData(previousData);

    const merged = normalizedCurrent.map((cur) => {
      const prev = normalizedPrevious.find((p) => p.label === cur.label);
      return {
        class: cur.label,
        current_quarter: cur.y,
        previous_quarter: prev ? prev.y : 0,
        course_level: cur.course_level,
      };
    });

    normalizedPrevious.forEach((prev) => {
      if (!merged.find((m) => m.class === prev.label)) {
        merged.push({
          class: prev.label,
          current_quarter: 0,
          previous_quarter: prev.y,
          course_level: prev.course_level,
        });
      }
    });

    const totals = merged.reduce(
      (acc, row) => {
        acc.current_quarter += row.current_quarter;
        acc.previous_quarter += row.previous_quarter;
        return acc;
      },
      { current_quarter: 0, previous_quarter: 0 }
    );
    totals.variance = totals.current_quarter - totals.previous_quarter;

    setMergedData(merged);
    setTotalRow(totals);
  }, [location.state?.currentData, location.state?.previousData]);

  const handlePrevious = () => {
    const viewToSet = title !== "Class Size" ? "reenrolment" : "class";

    const passedQuarters = location.state?.selectedQuarters || [
      "Q1 2025",
      "Q4 2024",
    ];
    const extractedQuarter = parseInt(
      passedQuarters[0].split(" ")[0].replace("Q", "")
    );
    const extractedYear = parseInt(passedQuarters[0].split(" ")[1]);

    console.log("extractedQuarter:", extractedQuarter);
    console.log("extractedYear:", extractedYear);

    navigate("/home", {
      state: {
        view: viewToSet,
        selectedQuarter: extractedQuarter,
        selectedYear: extractedYear,
      },
    });
  };

  const handleDetails = () => {
    navigate("/detailclass", {
      state: {
        selectedQuarters,
      },
    });
  };

  const calculateVariance = (current_quarter, previous_quarter) =>
    current_quarter - previous_quarter;

  const groupedData = mergedData.reduce((acc, row) => {
    const level = row.course_level || "Uncategorized";
    if (!acc[level]) acc[level] = [];
    acc[level].push(row);
    return acc;
  }, {});

  const entries = Object.entries(groupedData);
  const half = Math.ceil(entries.length / 2);
  const firstHalf = Object.fromEntries(entries.slice(0, half));
  const secondHalf = Object.fromEntries(entries.slice(half));

  const renderGroupedTable = (data) => (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        backgroundColor: "#1f1f1f",
        borderRadius: 6,
        overflow: "hidden",
        marginBottom: 20,
      }}
    >
      <thead style={{ backgroundColor: "#000" }}>
        <tr>
          <th style={thStyle}>CLASS</th>
          <th style={thStyle} className="text-center">
            {selectedQuarters[0]}
          </th>
          <th style={thStyle} className="text-center">
            {selectedQuarters[1]}
          </th>
          <th style={thStyle} className="text-center">
            VARIANCE
          </th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(data).map(([level, rows], idx) => {
          const levelTotals = rows.reduce(
            (acc, row) => {
              acc.current_quarter += row.current_quarter;
              acc.previous_quarter += row.previous_quarter;
              return acc;
            },
            { current_quarter: 0, previous_quarter: 0 }
          );
          levelTotals.variance =
            levelTotals.current_quarter - levelTotals.previous_quarter;

          return (
            <React.Fragment key={idx}>
              <tr>
                <td
                  colSpan={4}
                  style={{
                    ...tdStyle,
                    fontWeight: "bold",
                    backgroundColor: "#111",
                  }}
                >
                  {level}
                </td>
              </tr>
              {rows.map((row, i) => (
                <tr
                  key={i}
                  style={{
                    borderTop: "1px solid #444",
                    backgroundColor: i % 2 === 0 ? "#2b2b2b" : "#252525",
                  }}
                >
                  <td style={tdStyle}>{row.class}</td>
                  <td style={tdStyle} className="text-center">
                    {row.current_quarter}
                  </td>
                  <td style={tdStyle} className="text-center">
                    {row.previous_quarter}
                  </td>
                  <td style={tdStyle} className="text-center">
                    {calculateVariance(
                      row.current_quarter,
                      row.previous_quarter
                    )}
                  </td>
                </tr>
              ))}
              <tr>
                <td
                  style={{
                    ...tdStyle,
                    fontWeight: "bold",
                    backgroundColor: "#1a1a1a",
                  }}
                ></td>
                <td
                  style={{
                    ...tdStyle,
                    fontWeight: "bold",
                    textAlign: "center",
                    color: "#ffa726",
                    backgroundColor: "#1a1a1a",
                  }}
                >
                  {levelTotals.current_quarter}
                </td>
                <td
                  style={{
                    ...tdStyle,
                    fontWeight: "bold",
                    textAlign: "center",
                    color: "#ffa726",
                    backgroundColor: "#1a1a1a",
                  }}
                >
                  {levelTotals.previous_quarter}
                </td>
                <td
                  style={{
                    ...tdStyle,
                    fontWeight: "bold",
                    textAlign: "center",
                    color: "#ffa726",
                    backgroundColor: "#1a1a1a",
                  }}
                >
                  {levelTotals.variance}
                </td>
              </tr>
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <>
      <NavBar />
      <div
        style={{
          backgroundColor: "#373636",
          margin: 20,
          borderRadius: 10,
          padding: 30,
          minHeight: "calc(100vh - 80px)",
          color: "#fff",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 20,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <FaArrowLeft size={18} onClick={handlePrevious} />
            <h4 style={{ margin: 0 }}>{title}</h4>
            {selectedQuarters.map((q, index) => (
              <span
                key={index}
                style={{
                  background: "#444",
                  padding: "6px 12px",
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                {q}
              </span>
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {title !== "Attrition Rate" && (
              <button onClick={handleDetails} className="normal-button">
                <PiStudent size={18} />
              </button>
            )}

            <button
              onClick={() => console.log("Exporting...")}
              className="excel-button"
            >
              <FaFileExcel size={18} />
              Export to Excel
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 20 }}>
          <div style={{ flex: 1 }}>{renderGroupedTable(firstHalf)}</div>
          <div style={{ flex: 1 }}>{renderGroupedTable(secondHalf)}</div>
        </div>

        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            backgroundColor: "#1f1f1f",
            borderRadius: 6,
            overflow: "hidden",
          }}
        >
          <tbody>
            <tr>
              <td
                style={{
                  ...tdStyle,
                  fontWeight: "bold",
                  color: "#fff",
                  backgroundColor: "#1a1a1a",
                }}
              >
                TOTAL ({selectedQuarters[0]} vs {selectedQuarters[1]})
              </td>
              <td
                style={{
                  ...tdStyle,
                  fontWeight: "bold",
                  textAlign: "center",
                  backgroundColor: "#1a1a1a",
                  color: "#ffa726",
                }}
              >
                {totalRow.current_quarter}{" "}
                <span style={{ fontWeight: "normal", color: "#fff" }}>
                  ({selectedQuarters[0]})
                </span>
              </td>
              <td
                style={{
                  ...tdStyle,
                  fontWeight: "bold",
                  textAlign: "center",
                  backgroundColor: "#1a1a1a",
                  color: "#ffa726",
                }}
              >
                {totalRow.previous_quarter}{" "}
                <span style={{ fontWeight: "normal", color: "#fff" }}>
                  ({selectedQuarters[1]})
                </span>
              </td>
              <td
                style={{
                  ...tdStyle,
                  fontWeight: "bold",
                  textAlign: "center",
                  backgroundColor: "#1a1a1a",
                  color: "#ffa726",
                }}
              >
                {totalRow.variance}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};

const thStyle = {
  padding: "14px 12px",
  textAlign: "left",
  fontWeight: "bold",
  fontSize: 15,
  color: "#fff",
  borderBottom: "1px solid #444",
};

const tdStyle = {
  padding: "12px 12px",
  color: "#ddd",
  fontSize: 14,
};

export default Table;
